import type * as C from "@zooi/contracts";
import { campaignActionStatus } from "./campaign-items.ts";
import type { StatusKind } from "./ui/status-kinds.ts";

// A campaign's table (docs/design-system.md §0.4a, plan M8 step 8d), as plain
// TypeScript so it is tested without a DOM: rows grouped by playbook, what
// each row's state is, which rows can be selected, what a selection asks of
// the person, and the CSV of what the table shows.

export type UnitRow = C.CampaignUnits["rows"][number];
export type UnitPlaybook = C.CampaignUnits["playbooks"][number];
/** A candidate is not an action yet, so it has no status icon; a blocked one shows a lock. */
export type UnitState = { kind: "candidate" } | { kind: "picked" } | { kind: "blocked"; code: "unit_locked" | "unit_cooldown"; until: string | null } | { kind: "action"; status: StatusKind };
export type UnitFilter = { playbook: string | null; state: "all" | "candidates" | "actions" };

export function unitState(row: UnitRow): UnitState {
  if (row.action) return { kind: "action", status: campaignActionStatus({ status: row.action.status, reading: row.action.reading, measurement_mode: row.action.measurement_mode }) };
  const blocked = row.candidate?.blocked;
  if (blocked) return { kind: "blocked", code: blocked.code, until: blocked.until };
  return row.candidate?.picked_at ? { kind: "picked" } : { kind: "candidate" };
}

/** Only candidates nothing holds back, in an active campaign, can be selected. */
export const selectable = (row: UnitRow, open: boolean) => open && row.kind === "candidate" && !row.candidate?.blocked;

/** Rows by playbook, in the order a playbook first appears; rows without one come last. */
export function unitGroups(table: Pick<C.CampaignUnits, "rows" | "playbooks">, filter: UnitFilter): { playbook: UnitPlaybook | null; id: string | null; rows: UnitRow[] }[] {
  const groups = new Map<string | null, UnitRow[]>();
  for (const row of table.rows) {
    if (filter.playbook && row.playbook_id !== filter.playbook) continue;
    if (filter.state === "candidates" && row.kind !== "candidate") continue;
    if (filter.state === "actions" && row.kind !== "action") continue;
    groups.set(row.playbook_id, [...(groups.get(row.playbook_id) ?? []), row]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === null ? 1 : 0) - (b === null ? 1 : 0))
    .map(([id, rows]) => ({ id, playbook: table.playbooks.find((p) => p.id === id) ?? null, rows: sortRows(rows) }));
}

/** Candidates first (picked ones before the rest), then actions as the plans made them. */
function sortRows(rows: UnitRow[]): UnitRow[] {
  const rank = (row: UnitRow) => (row.kind === "action" ? 2 : row.candidate?.picked_at ? 0 : 1);
  return rows.map((row, index) => ({ row, index })).sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index).map(({ row }) => row);
}

/** What a selection asks of the person: how many rows, their time when every row says it, and how many a pre-authorization covers. */
export function selectionSummary(rows: readonly UnitRow[]): { count: number; minutes: number | null; covered: number; review: number } {
  const known = rows.every((row) => row.minutes !== null);
  return {
    count: rows.length,
    minutes: known ? rows.reduce((sum, row) => sum + (row.minutes ?? 0), 0) : null,
    covered: rows.filter((row) => row.candidate?.grant_id || row.candidate?.default_grant).length,
    review: rows.filter((row) => !(row.candidate?.grant_id || row.candidate?.default_grant)).length,
  };
}

/** The least a pre-authorization covering a group's candidates may still apply this week; null when none covers them. */
export function groupGrantRemaining(rows: readonly UnitRow[], grants: C.CampaignUnits["grants"]): number | null {
  const ids = new Set(rows.flatMap((row) => (row.candidate?.grant_id ? [row.candidate.grant_id] : [])));
  const left = grants.filter((grant) => ids.has(grant.id)).map((grant) => grant.remaining_this_week);
  return left.length ? Math.min(...left) : null;
}

/** An action's window as "day N of M" while it is observed; null otherwise. */
export function unitWindowDay(row: UnitRow, now: number): { day: number; of: number } | null {
  const a = row.action;
  if (!a?.window_days || !a.activated_at || !["live", "reading_due"].includes(a.status)) return null;
  const elapsed = Math.floor((now - Date.parse(a.activated_at)) / 86_400_000) + 1;
  return { day: Math.min(Math.max(elapsed, 1), a.window_days), of: a.window_days };
}

const csvCell = (value: string) => (/[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value);

/**
 * The CSV of the visible columns (design system §0.4a): no external text such
 * as a discussion's title, and no contacts. Cells the ledger cannot fill stay
 * empty, never 0.
 */
export function unitsCsv(columns: readonly { key: string; label: string }[], rows: readonly Record<string, string | null>[]): string {
  const lines = [columns.map((c) => csvCell(c.label)).join(","), ...rows.map((row) => columns.map((c) => csvCell(row[c.key] ?? "")).join(","))];
  return `${lines.join("\r\n")}\r\n`;
}
